// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useProviderAccess } from '../hooks/useProviderAccess';
import {
  ProviderAccessLayout,
  ProviderAccessWizard,
  ProviderCoAWizard,
  ProviderNewBusinessWizard,
} from '@loa/provider-form';
import { ProviderAccessLoading } from '../components/providerAccess/ProviderAccessLoading';
import { ProviderAccessError } from '../components/providerAccess/ProviderAccessError';
import { ProviderAccessRejected } from '../components/providerAccess/ProviderAccessRejected';
import { ProviderAccessSubmitted } from '../components/providerAccess/ProviderAccessSubmitted';
import { FeedbackPage } from '../components/providerAccess/FeedbackPage';

export function ProviderAccessPage() {
  const { token } = useParams<{ token: string }>();

  useEffect(() => {
    document.title = 'Provider – Submit Policy Details';
  }, []);

  if (!token) {
    return (
      <ProviderAccessError
        error={{
          error: 'No access token was provided. Please check the link in your email.',
          code: 'TOKEN_NOT_FOUND',
        }}
      />
    );
  }

  return <ProviderAccessPageContent token={token} />;
}

function ProviderAccessPageContent({ token }: Readonly<{ token: string }>) {
  const {
    context,
    pageState,
    error,
    submissionResult,
    submitting,
    formOpenDuration,
    submitForm,
    uploadDocument,
    rejectLoa,
    submitFeedback,
    acceptCoa,
    rejectCoa,
    issueNewBusiness,
    rejectNewBusiness,
    goToSubmitted,
    resetToActive,
  } = useProviderAccess(token);

  return (
    <ProviderAccessLayout context={context}>
      {pageState === 'loading' && <ProviderAccessLoading />}

      {pageState === 'error' && error && <ProviderAccessError error={error} />}

      {pageState === 'rejected' && context && (
        <ProviderAccessRejected
          brokerageName={context.brokerageName}
          brokerageLogoUrl={context.brokerageLogoUrl}
          rejectedAt={context.rejectedAt}
          documentType={context.documentType}
        />
      )}

      {pageState === 'submitted' && submissionResult && (
        <ProviderAccessSubmitted
          result={submissionResult}
          onSubmitAnother={resetToActive}
          brokerageName={context?.brokerageName}
          brokerageLogoUrl={context?.brokerageLogoUrl}
          documentType={context?.documentType}
        />
      )}

      {pageState === 'feedback' && context && (
        <FeedbackPage
          brokerageName={context.brokerageName}
          brokerageLogoUrl={context.brokerageLogoUrl}
          submitting={submitting}
          onSubmitFeedback={submitFeedback}
          onSkip={goToSubmitted}
        />
      )}

      {pageState === 'active' && context && (
        context.documentType === 'NEW_BUSINESS' ? (
          <ProviderNewBusinessWizard
            context={context}
            submitting={submitting}
            onIssue={issueNewBusiness}
            onReject={rejectNewBusiness}
          />
        ) : context.documentType === 'COA' ? (
          <ProviderCoAWizard
            context={context}
            submitting={submitting}
            onAccept={acceptCoa}
            onReject={rejectCoa}
          />
        ) : (
          <ProviderAccessWizard
            token={token}
            context={context}
            submitting={submitting}
            formOpenDuration={formOpenDuration}
            onSubmitForm={submitForm}
            onUploadDocument={uploadDocument}
            onRejectLoa={rejectLoa}
          />
        )
      )}
    </ProviderAccessLayout>
  );
}
